/**
 * Preprocesses assistant markdown so ASCII "box drawing" tables render correctly
 * while standard GFM pipe tables are left for remark-gfm.
 *
 * - GFM tables: rows like `| a | b |` with a separator row `| --- | --- |` — no `+` corners.
 * - ASCII grids: border lines like `+--------------------+` — wrap in a fenced `text` block
 *   so the renderer uses monospace + preserved whitespace.
 */

/**
 * Live SSE streams concatenate deltas into a single text part. Reloaded threads run
 * `mergeConsecutiveTextParts`, which joins persisted text segments with `\n\n`, so
 * headings/lists often parse correctly after refresh even when the model glued
 * `...sentence.### Heading` or `...sentence. - item` without a line break.
 * This normalizes those cases for streaming (and is idempotent for already-good text).
 */
export function normalizeMarkdownBlockBoundaries(markdown: string): string {
  if (!markdown) return markdown;
  let s = markdown.replaceAll("\r\n", "\n");

  s = normalizeStreamedAtxHeadings(s);

  // ATX headings glued to the previous line (requires newline before `#` for block headings).
  // Use `{2,6}` and exclude `#` before/inside the glue so `### Quick questions` is not split
  // into `#` + `## Quick…` (first `#` matched `[^\n]`, second segment matched `#{1,6}\s+`).
  // Also avoids corrupting table cells like `| # |`.
  s = s.replaceAll(/(?<![#\n])([^\n#])(#{2,6}\s+)/g, "$1\n$2");

  s = normalizeGluedGFMPipeTables(s);

  return s;
}

/**
 * CommonMark treats one line starting with `# ` as a single ATX heading until newline.
 * Streaming often glues `# What happened- I ...` or `# Verification table| Item |...`
 * into one line, producing one giant &lt;h1&gt; and empty stray `#` lines. Split those.
 */
export function normalizeStreamedAtxHeadings(markdown: string): string {
  if (!markdown) return markdown;
  const lines = markdown.split("\n");
  const out: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (/^#{1,6}\s*$/.test(trimmed)) {
      continue;
    }
    out.push(splitGluedAtxHeadingLine(line));
  }

  return out.join("\n");
}

function splitGluedAtxHeadingLine(line: string): string {
  const m = /^(#{1,6}\s+)(.+)$/.exec(line);
  if (!m) return line;
  const prefix = m[1];
  const rest = m[2];

  // "# Title | Col | Col |" — table must start on its own line.
  const pipeIdx = rest.indexOf("|");
  if (pipeIdx >= 4 && pipeIdx < rest.length - 24) {
    const tail = rest.slice(pipeIdx);
    if ((tail.match(/\|/g) ?? []).length >= 3) {
      const title = rest.slice(0, pipeIdx).trimEnd();
      if (title.length > 0) {
        return `${prefix}${title}\n\n${tail}`;
      }
    }
  }

  // "# Title- When I ..." or "# ... verification- `code`" — body glued after hyphen.
  const dm =
    /^(.+?)-\s+(When\b|If\b|I\s|The\s|After\b|A couple\b|`\s*\S)/i.exec(rest);
  if (dm && dm[1].length >= 3 && dm[1].length <= 400) {
    const rawTitle = dm[1];
    const titlePart = rawTitle.trimEnd();
    const bodyPart = rest.slice(rawTitle.length);
    return `${prefix}${titlePart}\n\n${bodyPart}`;
  }

  return line;
}

/**
 * Live SSE often concatenates chunks without newlines. GFM tables must start on a new
 * block line; otherwise remark parses everything as one paragraph and tables never form.
 * Refreshed threads usually merge text parts with `\\n\\n`, which avoids this.
 */
export function normalizeGluedGFMPipeTables(markdown: string): string {
  if (!markdown?.includes("|")) return markdown;
  let s = markdown;

  // Common model typo: "through2026-05-04" / "to2026-04-22" (missing space).
  s = s.replaceAll(/\bthrough(20\d{2}-\d{2}-\d{2})\b/g, "through $1");
  s = s.replaceAll(/\bto(20\d{2}-\d{2}-\d{2})\b/g, "to $1");

  // 1) Prose ending with punctuation glued to a pipe table row (e.g. "coherent.| Phase |").
  // Require ≥3 cells so we do not break inline "| x |" mentions.
  s = s.replaceAll(
    /([.!?])(\s*)(\|(?:\s*[^|\n]+\s*\|){3,})/g,
    "$1$2\n\n$3",
  );

  // 1b) Colon (often "these next:") glued to a 2-column pipe table — rule (1) only matched
  // 3+ cells, so "next:| Option | Outcome |" stayed one line and the table never parsed.
  // Guard: only trigger when `:` follows a letter (prose), not delimiter syntax like `---:`.
  s = s.replaceAll(
    /((?<=[A-Za-z]):)([ \t]*)(?=(\|(?:\s*[^|\n]+\s*\|){2,}))/g,
    "$1\n\n$2",
  );

  // 2) Row glued to separator: "| Reasoning ||---|---|---|..." → newline before "---".
  s = s.replaceAll(/\|\|---/g, "\n|---");

  // 3) Row boundary when newlines were dropped: "...|---|| Platform ..." or "|| Platform ...".
  // Use only horizontal whitespace between the two pipes — `\s*` would match newline and
  // falsely join `|---|\n| Row` (delimiter row + next row), stripping the delimiter's `|`.
  s = s.replaceAll(/\|[ \t]*\|\s+(?=[A-Za-z0-9])/g, "\n| ");

  // 4) Last table row fused to following paragraph (same line only). Use `[ \t]+`: `\s+`
  // matches newlines and would steal the closing `|` before `\n\nIf you want…` after a row.
  s = s.replaceAll(
    /(\d{4}-\d{2}-\d{2}[^\n|]*\|)[ \t]+(A couple of items\b)/gi,
    "$1\n\n$2",
  );
  s = s.replaceAll(/\|[ \t]+(They all show\b)/gi, "\n\n$1");
  s = s.replaceAll(/\|[ \t]+(If you want\b)/gi, "\n\n$1");

  // Sentence glued to "If you want" (no newline before closing paragraph).
  s = s.replaceAll(/([.!?])(If you want\b)/gi, "$1\n\n$2");

  // "Verification table| Item |" style when not caught by heading split (plain prose).
  s = s.replaceAll(
    /(table)(\|\s*Item\s*\|)/gi,
    "$1\n\n$2",
  );

  return s;
}

/**
 * Streaming models often split the **delimiter row** across SSE chunks. That yields multiple
 * consecutive lines containing only `|`, `-`, `:`, and spaces — e.g. `|---|---|` then
 * `---|---|---|---|---|` on the next line. The second line does not start with `|`, so remark-gfm
 * stops the table after the first separator fragment + first data row; later `|` rows render as
 * raw text. Collapse those fragments into one physical line before parse.
 *
 * @see stream-part17.txt (assignment agent): separator spread across lines 1323–1326.
 * @see stream-part21.txt: `|---` then `---|---|---|` must not become `|------` (strip one `---`).
 */
function isGFMTableDelimiterStructuralLine(line: string): boolean {
  const t = line.trim();
  if (!t.includes("|") || !/---/.test(t)) return false;
  return /^[|\-\s:]+$/.test(t);
}

function joinGFMTableDelimiterFragments(acc: string, nextLine: string): string {
  const accEnd = acc.trimEnd();
  const nextStart = nextLine.trimStart();
  // First fragment ended mid-cell (`|---`); next begins with `---…` → avoid `|------`.
  if (!accEnd.endsWith("|") && nextStart.startsWith("-")) {
    return acc + nextLine.replace(/^\s*---/, "");
  }
  return acc + nextLine;
}

export function mergeFragmentedGFMTableDelimiterLines(markdown: string): string {
  if (!markdown?.includes("|") || !markdown.includes("-")) return markdown;
  const lines = markdown.split("\n");
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    let acc = lines[i];
    while (
      i + 1 < lines.length &&
      isGFMTableDelimiterStructuralLine(acc) &&
      isGFMTableDelimiterStructuralLine(lines[i + 1])
    ) {
      acc = joinGFMTableDelimiterFragments(acc, lines[i + 1]);
      i++;
    }
    out.push(acc);
    i++;
  }
  return out.join("\n");
}

/**
 * SSE chunks often break a single GFM pipe row across physical lines: the first segment
 * ends mid-cell (no trailing `|`), and the next line continues with prose + `|` but does
 * not start with `|`, so remark-gfm ends the table early. Join those continuation lines
 * until the row ends with `|`.
 *
 * Continuations may start with whitespace then `|` (wrapped cell tail like ` | Why text |`);
 * those must not be mistaken for a new row (`trimStart()` would begin with `|`).
 *
 * @see stream-part18.txt ~1564–1565 ("Architecture" / " and rollout planning…").
 * @see stream-part19.txt ~2380–2383 (`| Aisha Khan | 2` + ` | Light-to-moderate…`).
 */
function isIncompleteGFMPipeTableDataRow(line: string): boolean {
  const trimmedEnd = line.trimEnd();
  if (!/^\s*\|/.test(line)) return false;
  if (trimmedEnd.endsWith("|")) return false;
  if (isGFMTableDelimiterStructuralLine(line)) return false;
  return true;
}

/** `| col |` starts a row in column 0; ` | cell tail |` is a wrapped fragment (leading space). */
function isStreamedGFMPipeTableNewRowLine(line: string): boolean {
  if (/^\s+\|/.test(line)) return false;
  return line.trimStart().startsWith("|");
}

/**
 * Last column split onto its own line (stream-part20): previous segment ends with `|` after
 * "Intermediate" but the rate "40 |" is still to come. One physical `|` closed a cell too early.
 */
function isLikelySplitTrailingPipeTableCell(peek: string): boolean {
  const t = peek.trim();
  if (!t.endsWith("|")) return false;
  if ((peek.match(/\|/g) ?? []).length !== 1) return false;
  if (isStreamedGFMPipeTableNewRowLine(peek)) return false;
  // Hourly rate / compact numeric last cell
  return /^\s*\$?[\d.,]+\s*(?:\/\s*hr)?\s*\|\s*$/i.test(peek);
}

function mergeStreamSplitGFMPipeTableDataRows(markdown: string): string {
  if (!markdown?.includes("|")) return markdown;
  const lines = markdown.split("\n");
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!isIncompleteGFMPipeTableDataRow(line)) {
      out.push(line);
      i += 1;
      continue;
    }

    let merged = line;
    i += 1;
    while (i < lines.length) {
      const next = lines[i];
      if (next.trim() === "") break;
      if (isStreamedGFMPipeTableNewRowLine(next)) break;
      merged = `${merged.trimEnd()} ${next.trim()}`;
      i += 1;
      if (merged.trimEnd().endsWith("|")) {
        const peek = i < lines.length ? lines[i] : "";
        if (peek.trim() !== "" && isLikelySplitTrailingPipeTableCell(peek)) {
          merged = `${merged.replace(/\|\s*$/, "").trimEnd()} | ${peek.trim()}`;
          i += 1;
        }
        break;
      }
    }
    out.push(merged);
  }

  return out.join("\n");
}

/** GFM separator cell: :---, ---, :---:, etc. */
const GFM_SEP_CELL = /^\s*:?-{3,}:?\s*$/;

function countPipeTableCells(line: string): number {
  const t = line.trim();
  if (!t.startsWith("|") || !t.endsWith("|")) return 0;
  return t.slice(1, -1).split("|").length;
}

function isGfmSeparatorRow(line: string): boolean {
  const t = line.trim();
  if (!t.startsWith("|") || !t.endsWith("|")) return false;
  const parts = t
    .slice(1, -1)
    .split("|")
    .map((c) => c.trim());
  if (parts.length < 2) return false;
  return parts.every((c) => c === "" || GFM_SEP_CELL.test(c));
}

/**
 * When SSE splits the delimiter, the first line can be a **truncated** structural row
 * (e.g. `|---|---|---:` with no closing `|`) and the next line is a full `| --- | ... |` row.
 * `mergeFragmentedGFMTableDelimiterLines` does not merge across blank lines, so both remain
 * and remark never forms a table. Also delete blank lines between the junk fragment and
 * a valid separator so the header is adjacent to a single delimiter row.
 *
 * @see stream-scratchpad (Updated schedule summary): `|---|---|---:` then `| --- | ... |`
 */
function removeBrokenGFMTableDelimiterClutter(markdown: string): string {
  if (!markdown?.includes("|") || !markdown.includes("-")) return markdown;
  const lines = markdown.split("\n");
  const out: string[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!isGFMTableDelimiterStructuralLine(line) || isGfmSeparatorRow(line)) {
      out.push(line);
      i += 1;
      continue;
    }
    // Structural dash-only line that is not a full GFM separator.
    let j = i + 1;
    while (j < lines.length && lines[j].trim() === "") j += 1;
    if (j < lines.length && isGfmSeparatorRow(lines[j])) {
      // Drop this fragment and any blank lines before the real separator.
      out.push(lines[j]);
      i = j + 1;
      continue;
    }
    out.push(line);
    i += 1;
  }

  return out.join("\n");
}

function findNextPipeTableColCount(
  lines: readonly string[],
  separatorIndex: number,
): number {
  for (let j = separatorIndex + 1; j < lines.length; j++) {
    const next = lines[j].trim();
    if (next === "") continue;
    if (isGfmSeparatorRow(lines[j])) break;
    if (next.startsWith("|") && next.endsWith("|")) {
      return countPipeTableCells(lines[j]);
    }
    break;
  }
  return 0;
}

function rewriteGfmSeparatorLine(line: string, nextRowColCount: number): string {
  const t = line.trim();
  const dashParts = t
    .slice(1, -1)
    .split("|")
    .map((c) => c.trim())
    .filter((c) => c !== "");
  if (
    dashParts.length < 2 ||
    !dashParts.every((c) => GFM_SEP_CELL.test(c))
  ) {
    return line;
  }

  const cols = countPipeTableCells(line);
  if (nextRowColCount >= 2 && cols === nextRowColCount) {
    return line;
  }

  if (nextRowColCount >= 2 && cols !== nextRowColCount) {
    const sep = Array.from({ length: nextRowColCount }, () => " --- ").join("|");
    return `|${sep}|`;
  }

  return line;
}

/**
 * Models sometimes emit malformed separator rows like `|---|---||` (extra trailing `|`),
 * which breaks remark-gfm table parsing. Rewrites separator-only lines to match the
 * following pipe row's column count when detectable.
 */
export function normalizeBrokenGFMPipeTableSeparators(markdown: string): string {
  if (!markdown?.includes("|")) return markdown;
  const lines = markdown.split("\n");
  const out: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!isGfmSeparatorRow(line)) {
      out.push(line);
      continue;
    }
    const nextCols = findNextPipeTableColCount(lines, i);
    out.push(rewriteGfmSeparatorLine(line, nextCols));
  }

  return out.join("\n");
}

/** Top/bottom border of an ASCII grid: +---+ or +----+----+----+ */
function isAsciiBorderLine(line: string): boolean {
  const t = line.trim();
  if (!t.startsWith("+")) return false;
  // Only box-drawing chars (no letters); must include '-' so we don't match "+1+2+"
  if (!t.includes("-")) return false;
  return /^\+[\s\-+]*$/.test(t);
}

/** Cell row in both GFM and ASCII; we only group these after an ASCII border started the block. */
function isPipeRow(line: string): boolean {
  const t = line.trim();
  if (t.length === 0) return false;
  return t.startsWith("|") && t.endsWith("|");
}

function collectAsciiTableEnd(
  lines: readonly string[],
  start: number,
): number {
  let end = start + 1;
  while (end < lines.length) {
    const line = lines[end];
    const t = line.trim();
    if (t === "") break;
    if (isAsciiBorderLine(line) || isPipeRow(line)) {
      end += 1;
      continue;
    }
    break;
  }
  return end;
}

const FENCE = "```";

/** Remove blank lines inside GFM pipe tables and collapse excessive vertical whitespace. */
export function sanitizeGFMPipeTableMarkdown(markdown: string): string {
  if (!markdown) return markdown;

  const collapsed = markdown.replace(/\n{3,}/g, "\n\n");
  const lines = collapsed.split("\n");
  const out: string[] = [];
  let inTable = false;

  for (const line of lines) {
    const trimmed = line.trim();
    const isPipeRow = trimmed.startsWith("|");

    if (isPipeRow) {
      inTable = true;
      out.push(line);
      continue;
    }

    if (inTable && trimmed === "") {
      continue;
    }

    inTable = false;
    out.push(line);
  }

  return out.join("\n");
}

/**
 * Stream + GFM fixes applied before remark-parse (block boundaries, glued pipe tables,
 * separator repair). Used by persisted-message merge and by the remark `parse` wrapper
 * for live `MarkdownTextPrimitive`.
 */
export function preprocessMarkdownForGfmParse(markdown: string): string {
  return sanitizeGFMPipeTableMarkdown(
    normalizeBrokenGFMPipeTableSeparators(
      normalizeMarkdownBlockBoundaries(
        mergeStreamSplitGFMPipeTableDataRows(
          mergeFragmentedGFMTableDelimiterLines(
            removeBrokenGFMTableDelimiterClutter(markdown),
          ),
        ),
      ),
    ),
  );
}

/**
 * Wraps contiguous ASCII grid blocks (starting with a `+---` style border) in fenced
 * code blocks so columns align. Leaves GFM-only tables (no `+` border lines) unchanged.
 */
export function prepareMarkdownForAsciiGrids(markdown: string): string {
  const normalized = preprocessMarkdownForGfmParse(markdown);
  if (!normalized.includes("+")) return normalized;

  // Avoid double-wrapping when this string (or a slice) was already normalized.
  if (/^`{3,}text\s*\r?\n/m.test(normalized.trimStart())) {
    return normalized;
  }

  const lines = normalized.split("\n");
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    if (isAsciiBorderLine(lines[i])) {
      const end = collectAsciiTableEnd(lines, i);
      const block = lines.slice(i, end);
      if (block.length >= 2) {
        const body = block.join("\n");
        const fenceChar = body.includes(FENCE) ? "````" : FENCE;
        out.push(`${fenceChar}text\n${body}\n${fenceChar}`);
        i = end;
        continue;
      }
    }
    out.push(lines[i]);
    i += 1;
  }

  return out.join("\n");
}
